import { Dropdown, MenuProps, Row } from "antd";
import { useCallback, useMemo, useState, MouseEvent } from "react";
import { usePermissions } from "../../../../../../../lib/hooks/usePermissions";
import { useDefaultDeleteModal } from "../../../../../../../lib/hooks/defaultDeleteModal";
import { EllipsisOutlined } from "@ant-design/icons";
import { deleteEmail } from "../../../../../../../acions/emails";
import { useResend } from "../../../../../../../components/EmailModal/hooks/useResend";

type TableActionsProps = {
  record: any;
  setItemToOpen: React.Dispatch<
    React.SetStateAction<{
      id: string;
      type: 1 | 2 | 3;
    } | null>
  >;
  reloadData: () => void;
};

export const TableActions = ({
  record,
  setItemToOpen,
  reloadData,
}: TableActionsProps) => {
  const { openDeleteModal } = useDefaultDeleteModal(deleteEmail, reloadData);

  const onView = useCallback(
    (id: string, type: 1 | 2 | 3) => setItemToOpen({ id, type }),
    [setItemToOpen],
  );

  const [isSending, setIsSending] = useState<boolean>(false);

  const { resend } = useResend(reloadData, setIsSending);

  const onDelete = useCallback(
    (e: MouseEvent<HTMLDivElement>, req: { id: string; type: 0 | 1 | 2 }) => {
      e.stopPropagation();
      openDeleteModal(req, "Delete Mailing");
    },
    [openDeleteModal],
  );

  const onResend = useCallback(
    (e: MouseEvent<HTMLDivElement>): void => {
      e.stopPropagation();
      !isSending && resend(e.currentTarget.id);
    },
    [isSending, resend],
  );

  const getDeleteController = useCallback((type: 1 | 2 | 3): string => {
    if (type === 1) {
      return "MailingEmails";
    }

    return type === 2 ? "MailingLetters" : "MailingScheduled";
  }, []);

  const { hasAccess } = usePermissions();

  const showDelete: boolean = useMemo(() => {
    const { type, status } = record;

    const showDelete: boolean = type === 1 ? ![2, 3].includes(status) : true;

    return showDelete && hasAccess(getDeleteController(type), "Delete");
  }, [hasAccess, record, getDeleteController]);

  const showResend: boolean = useMemo(() => {
    const { status, type } = record;

    return type !== 2 && status === 2 && hasAccess("MailingEmails", "Resend");
  }, [record, hasAccess]);

  const items: MenuProps["items"] = useMemo(() => {
    const { id, type } = record;

    const value: MenuProps["items"] = [
      {
        key: "1",
        label: <Row onClick={() => onView(id, type)}>View</Row>,
      },
    ];

    showResend &&
      value.push({
        key: "2",
        label: (
          <Row id={id} onClick={onResend}>
            Resend
          </Row>
        ),
      });

    showDelete &&
      value.push({
        key: "3",
        label: <Row onClick={(e) => onDelete(e, { id, type })}>Delete</Row>,
      });

    return value;
  }, [record, onDelete, onView, showDelete, showResend, onResend]);

  return (
    <Dropdown menu={{ items }} trigger={["click"]}>
      <EllipsisOutlined className="ml-1" onClick={(e) => e.stopPropagation()} />
    </Dropdown>
  );
};
